import { COURSE_TYPES, STATUSES } from 'constants/common';

import { getCourseType } from 'components/CoursesTable/CoursesTable.helpers';

import { getTranslation } from 'helpers/getTranslation';

import * as api from 'api/methods';

export type LessonRatingsCourseSchool = {
  id: string;
  name: string;
  preview?: string | null;
};

export type LessonRatingsCourse = {
  id: string;
  title?: string | null;
  status?: string;
  type?: string;
  school?: LessonRatingsCourseSchool | null;
};

export type LessonRatingsCourseOption = {
  value: string;
  label: string;
  courseTitle: string;
  typeLabel: string;
  school: LessonRatingsCourseSchool | null;
};

const ALL_COURSE_TYPES = [
  COURSE_TYPES.COURSE,
  COURSE_TYPES.TRAINING,
  COURSE_TYPES.ARCADE,
  COURSE_TYPES.DAILY_CHALLENGE,
  COURSE_TYPES.LEARNING_PATH,
];

const LIST_PARAMS = { status: STATUSES.PUBLISHED, limit: 1000, page: 1 };

export const getEmptyAggregatedData = () => ({
  courseAverage: 0,
  totalCount: 0,
  distribution: [1, 2, 3, 4, 5].map((star: number) => ({ star, count: 0 })),
  modules: [] as any[],
});

export const formatRatingScore = (value: number): string =>
  value.toFixed(1).replace('.', ',');

export const getRatingBarColor = (averageRating: number) => {
  if (averageRating >= 4) {
    return '#85E0AB';
  }

  if (averageRating >= 3) {
    return '#FDCE82';
  }

  return '#F98A8A';
};

export const getModuleTitle = (title: string | null) =>
  title || getTranslation('common.text.untitled');

export const getLessonTitle = (title: string | null) =>
  title || getTranslation('common.text.untitled');

const getCourseTitle = (title?: string | null) =>
  title || getTranslation('common.text.untitled');

const mapSchool = (
  school?: LessonRatingsCourseSchool | null,
): LessonRatingsCourseSchool | null => {
  if (!school) {
    return null;
  }

  return {
    id: school.id,
    name: school.name,
    preview: school.preview,
  };
};

const mapSchoolFromApi = (school: any): LessonRatingsCourseSchool | null => {
  if (!school?.id && !school?._id) {
    return null;
  }

  return {
    id: school.id ?? school._id,
    name: school.name,
    preview: school.preview,
  };
};

const extractResponseData = (response: unknown): any[] => {
  if (Array.isArray(response)) {
    return response;
  }

  if (
    response &&
    typeof response === 'object' &&
    Array.isArray((response as { data?: unknown }).data)
  ) {
    return (response as { data: any[] }).data;
  }

  return [];
};

const mapCourseFromApi = (
  course: any,
  courseType: string,
): LessonRatingsCourse => ({
  id: course.id,
  title: course.title,
  status: course.status,
  type: course.type || courseType,
  school: mapSchoolFromApi(course.school),
});

const fetchCoursesByTypeForLessonRatings = async (
  courseType: string,
  { isRootAdmin, schoolId }: { isRootAdmin: boolean; schoolId: string },
): Promise<LessonRatingsCourse[]> => {
  switch (courseType) {
    case COURSE_TYPES.TRAINING: {
      const response = isRootAdmin
        ? await api.getTrainings(LIST_PARAMS)
        : await api.getSchoolTrainings({
            schoolId,
            params: LIST_PARAMS,
          });

      return extractResponseData(response).map((course) =>
        mapCourseFromApi(course, courseType),
      );
    }
    case COURSE_TYPES.ARCADE: {
      const response = isRootAdmin
        ? await api.getArcades(LIST_PARAMS)
        : await api.getSchoolArcades({
            schoolId,
            params: LIST_PARAMS,
          });

      return extractResponseData(response).map((course) =>
        mapCourseFromApi(course, courseType),
      );
    }
    case COURSE_TYPES.DAILY_CHALLENGE: {
      const response = isRootAdmin
        ? await api.getDailyChallenges(LIST_PARAMS)
        : await api.getSchoolDailyChallenges({
            schoolId,
            params: LIST_PARAMS,
          });

      return extractResponseData(response).map((course) =>
        mapCourseFromApi(course, courseType),
      );
    }
    default: {
      const response = isRootAdmin
        ? await api.getCourses({ ...LIST_PARAMS, type: courseType })
        : await api.getSchoolCourses({
            schoolId,
            params: { ...LIST_PARAMS, type: courseType },
          });

      return extractResponseData(response).map((course) =>
        mapCourseFromApi(course, courseType),
      );
    }
  }
};

export const fetchPublishedCoursesForLessonRatings = async ({
  isRootAdmin,
  schoolId,
}: {
  isRootAdmin: boolean;
  schoolId: string;
}): Promise<LessonRatingsCourse[]> => {
  const coursesByType = await Promise.all(
    ALL_COURSE_TYPES.map((courseType) =>
      fetchCoursesByTypeForLessonRatings(courseType, { isRootAdmin, schoolId }),
    ),
  );

  const coursesById = new Map<string, LessonRatingsCourse>();

  for (const course of coursesByType.flat()) {
    if (course.status !== STATUSES.PUBLISHED) {
      continue;
    }

    if (!coursesById.has(course.id)) {
      coursesById.set(course.id, course);
    }
  }

  return [...coursesById.values()].sort((leftCourse, rightCourse) =>
    (leftCourse.title || '').localeCompare(rightCourse.title || '', undefined, {
      sensitivity: 'base',
    }),
  );
};

export const prepareLessonRatingsCourseOptions = (
  courses: LessonRatingsCourse[],
): LessonRatingsCourseOption[] =>
  courses.map((course) => {
    const courseTitle = getCourseTitle(course.title);
    const typeLabel = getCourseType(course.type);

    return {
      value: course.id,
      label: courseTitle,
      courseTitle,
      typeLabel,
      school: mapSchool(course.school),
    };
  });

export const getCurrentLessonRatingsCourseOption = (
  course: LessonRatingsCourse | null | undefined,
): LessonRatingsCourseOption | null => {
  if (!course) {
    return null;
  }

  const courseTitle = getCourseTitle(course.title);
  const typeLabel = getCourseType(course.type);

  return {
    value: course.id,
    label: courseTitle,
    courseTitle,
    typeLabel,
    school: mapSchool(course.school),
  };
};
